import cl from 'classnames';
import * as React from 'react';
import styled from 'styled-components';
import { betweenScreen } from '@doltech/core/lib/responsive/responsive.util';
import { IconContentList } from '@doltech/icons/IconContentList';
import { IconCourseInfoOutline } from '@doltech/icons/IconCourseInfoOutline';
import { IconOtherClose } from '@doltech/icons/IconOtherClose';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { Box } from '@doltech/ui/lib/figma/Common/BoxV2';
import { TOCDrawerProps } from '@doltech/ui/lib/figma/Layout/Landing/Content/TableOfContent/TOCDrawer/TOCDrawer';
import { shadows } from '@doltech/ui/lib/figma/shadows';
import { TOCFooter } from './TOCFooter';
import { IconSystemV2EssentialClose } from '@doltech/icons/SystemV2/IconSystemV2EssentialClose';
import { DolPopover } from '@doltech/ui/lib/figma/Popover/DolPopover';
import { scrollBarStylesGeneral } from '@doltech/utils/lib/css-style';

const CloseButton = styled.div`
  cursor: pointer;
  position: absolute;
  top: 15px;
  right: 30px;
`;

const LAYOUT = {
  FILL: 'fill',
  RESPONSIVE: 'responsive',
} as const;

const Wrapper = styled.div`
  border-radius: 12px;
  padding: 16px;
  height: 75vh;
  overflow: auto;
  ${scrollBarStylesGeneral()};

  .user-info {
    width: 320px;
  }
`;

const Action = styled.div`
  padding: 8px;
  border-radius: 50%;

  &.active {
    width: 40px;
    height: 40px;
    background-color: ${colorsV2.neutral160};
  }
`;

const ActionGroup = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  width: 100%;
`;

const Container = styled.aside`
  display: none;
  position: fixed;
  bottom: 20px;
  right: 16px;
  min-height: 52px;
  background-color: ${colorsV2.white100};
  border-top: 1px solid ${colorsV2.gray20};
  border-radius: 40px;
  box-shadow: ${shadows.zIndex4};
  z-index: 10;

  ${betweenScreen(451, 1239)} {
    display: block;
  }
`;

export interface FloatingButtonProps extends Pick<TOCDrawerProps, 'scrollContainerElm'> {
  layout?: typeof LAYOUT[keyof typeof LAYOUT];
  pageId?: string;
  pageUrl?: string;
  tooltipColor?: string;
  tocTitle?: string;
  tocItems?: TOCDrawerProps['items'];
  tocEditorItems?: TOCDrawerProps['editorItems'];
  detailInfo?: React.ReactNode;
}

export const FloatingButton = (props: FloatingButtonProps) => {
  const {
    layout = LAYOUT.FILL,
    tocTitle,
    tocItems,
    tocEditorItems,
    scrollContainerElm,
    detailInfo,
  } = props;

  const isFill = layout === LAYOUT.FILL;
  const [tocVisible, setTocVisible] = React.useState(false);
  const [infoVisible, setInfoVisible] = React.useState(false);

  const isValidTocItems = tocEditorItems?.length > 0 || tocItems?.length > 0;

  return (
    <Container>
      <ActionGroup maxWidth={isFill ? '100%' : ['100%', '140px']}>
        <Action className={cl({ active: infoVisible })}>
          <DolPopover
            placement="left-start"
            trigger="click"
            overlayClassName="dol-popover user-info"
            visible={infoVisible}
            onVisibleChange={setInfoVisible}
            content={
              <Wrapper>
                <CloseButton onClick={() => setInfoVisible(false)}>
                  <IconOtherClose fontSize={20}/>
                </CloseButton>
                {detailInfo}
              </Wrapper>
            }
          >
            {infoVisible ?
              <IconSystemV2EssentialClose size={24} color="white100"/> :
              <IconCourseInfoOutline size={24} color="gray180"/>
            }
          </DolPopover>
        </Action>
      </ActionGroup>
      {isValidTocItems && (
        <ActionGroup maxWidth="96px">
          <Action className={cl({ active: tocVisible })}>
            <DolPopover
              placement="left-start"
              trigger="click"
              overlayClassName="dol-popover"
              onVisibleChange={setTocVisible}
              visible={tocVisible}
              content={
                <Wrapper>
                  <TOCFooter
                    onClose={(e) => {
                      e?.stopPropagation();
                      setTocVisible(false);
                    }}
                    tocTitle={tocTitle}
                    items={tocItems}
                    editorItems={tocEditorItems}
                    scrollContainerElm={scrollContainerElm}
                  />
                </Wrapper>
              }
            >
              {tocVisible ?
                <IconSystemV2EssentialClose size={24} color="white100"/> :
                <IconContentList size={24} color="gray180"/>
              }
            </DolPopover>
          </Action>
        </ActionGroup>
      )}
    </Container>
  );
};
